<template>
  <!-- Icon-only mode: a bare square button with no .btn chrome -->
  <button
    v-if="iconOnly"
    ref="btnEl"
    type="button"
    class="btn-icon"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
  >
    <AppSpinner v-if="loading" size="sm" />
    <slot v-else />
  </button>

  <button
    v-else
    ref="btnEl"
    :type="type"
    class="btn"
    :class="[variantClass, size === 'sm' && 'btn-sm', !block && 'btn-auto']"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
  >
    <AppSpinner v-if="loading" size="sm" aria-hidden="true" />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import AppSpinner from "./AppSpinner.vue"

/**
 * The one button. Wraps the global .btn vocabulary so call sites stop
 * mutating it inline (width:auto snippets, inline ruby backgrounds,
 * hand-rolled icon buttons). Attributes (aria-label, @click, …) fall
 * through to the underlying <button>.
 */
const props = withDefaults(
  defineProps<{
    /**
     * "danger" is a solid red fill — for the one destructive action a screen is
     * about. Use "danger-outline" for a destructive action that merely sits in
     * a row among others (remove a member, drop a connection): the weight
     * belongs on the confirm dialog, not on every row of a list.
     */
    variant?: "primary" | "secondary" | "outline" | "danger" | "danger-outline" | "warning" | "ghost" | "link"
    size?: "sm" | "md"
    /** Full-width (the raw .btn default). Off here: most buttons are inline. */
    block?: boolean
    /** Bare square icon button (no background/border) — pass an aria-label. */
    iconOnly?: boolean
    loading?: boolean
    disabled?: boolean
    type?: "button" | "submit" | "reset"
  }>(),
  {
    variant: "primary",
    size: "md",
    block: false,
    iconOnly: false,
    loading: false,
    disabled: false,
    type: "button",
  },
)

const variantClass = computed(
  () =>
    ({
      primary: "",
      secondary: "btn-secondary",
      outline: "btn-outline",
      danger: "btn-danger",
      "danger-outline": "btn-danger-outline",
      warning: "btn-warning",
      ghost: "btn-ghost",
      link: "btn-link",
    })[props.variant],
)

// Exposed so wrappers (e.g. ConfirmDialog focusing its cancel button) can
// drive focus through the component instead of reaching for a raw <button>.
const btnEl = ref<HTMLButtonElement | null>(null)
defineExpose({ focus: () => btnEl.value?.focus() })
</script>
